<script setup lang="ts">
/**
 * AppIcon
 *
 * A component for displaying a Heroicon or functional icon in various shapes, sizes, and color schemes.
 *
 * NOTE(spk): Shortcut color theming is not yet available, use the bg-color and icon-color
 * props to set a colorway that is consistent with existing use cases and brand design guidelines.
 *
 * Sizing follows the tailwind font sizing scheme and relies on the background shape to provide
 * bulk when sized up as recommened by Tailwind CSS guides.
 *
 * Activity badges are supported. The default max numeric value is capped by the
 * ActivityBadge component.  If there is a high value use case to display a value beyond the
 * cap, use a string value for the activity badge instead.  Ex: "10,000+"
 *
 * Usage:
 * import AppIcon from "@/components/shared/AppIcon.vue"
 * import { SpeakerphoneIcon } from "@heroicons/vue/outline"
 *
 * TODO(spk): provide common colorways that fit with the brand design guidelines for convenience and consistency.
 *
 * <template>
 *   <AppIcon
 *     :activity-badge="5"
 *     :icon="SpeakerphoneIcon"
 *     icon-color="text-white"
 *     bg-color="bg-xy-blue"
 *     size="lg"
 *     type="squircle"
 *   />
 * </template>
 */

import ActivityBadge from "@/components/shared/ActivityBadge.vue"
import { computed } from "vue"
import type { Component, FunctionalComponent, RenderFunction } from "vue"

const props = withDefaults(
  defineProps<{
    activityBadge?: string | number | boolean
    activityBadgeMax?: number
    bgColor?: `bg-${string}`
    iconColor?: `text-${string}`
    icon: FunctionalComponent | Component | RenderFunction
    size?: "xs" | "sm" | "base" | "lg" | "xl" | "2xl"
    type?: "circle" | "square" | "squircle"
  }>(),
  {
    activityBadge: false,
    activityBadgeMax: undefined,
    bgColor: "bg-xy-blue",
    iconColor: "text-white",
    size: "base",
    type: "squircle",
  }
)

const size = computed(() => {
  const sizes = {
    xs: { bg: "w-6 h-6", icon: "w-3 h-3" },
    sm: { bg: "w-8 h-8", icon: "w-4 h-4" },
    base: { bg: "w-10 h-10", icon: "w-5 h-5" },
    lg: { bg: "w-12 h-12", icon: "w-6 h-6" },
    xl: { bg: "w-14 h-14", icon: "w-7 h-7" },
    "2xl": { bg: "w-16 h-16", icon: "w-8 h-8" },
  }

  return sizes[props.size]
})

const rounding = computed(() => {
  let rounded = ""
  switch (props.type) {
    case "circle":
      rounded = "rounded-full"
      break
    case "square":
      rounded = "rounded-lg"
      break
    case "squircle":
      rounded = ""
      break
  }

  return rounded
})
</script>

<template>
  <div
    aria-hidden="true"
    :class="['aspect-square leading-none relative', size.bg]"
  >
    <div
      :class="[
        'absolute inset-0',
        type === 'squircle' ? 'bg-squircle' : '',
        rounding,
        bgColor,
        size.bg,
      ]"
    />

    <div class="flex flex-col h-full w-full items-center justify-center">
      <div
        :class="[size.icon]"
        class="flex flex-col items-center justify-center relative"
      >
        <component :is="icon" :class="['relative', iconColor]" />
        <div v-if="$slots['default']" class="sr-only">
          <slot />
        </div>
      </div>
    </div>

    <!--
    NOTE(spk): Positioning activity badges according to the shape of the icon
    is not straightforward given the static height but unknown width of the badge
    relative to the "size" of the icon.  Using a combination of translate fractions
    which is relative to dimensions of the badge and margin which is relative to the 
    containing block we can achive a general standard of positioning - tread lightly 
    when making changes.
    -->
    <div
      :class="[
        'flex absolute z-[5]',
        type === 'circle' &&
          'bottom-full -mb-[25%] left-full -translate-x-1/4 -ml-[25%]',
        type === 'squircle' &&
          'bottom-full -mb-[25%] left-full -translate-x-1/4 -ml-[25%]',
        type === 'square' && 'top-0 translate-x-1/2 right-0 -translate-y-1/2',
      ]"
    >
      <ActivityBadge :max="activityBadgeMax" :value="activityBadge" />
    </div>
  </div>
</template>

<style lang="css" scoped>
/**
 * NOTE(spk): source file located /client/src/assets/squircle.svg
 */
.bg-squircle {
  mask-image: url("data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz48c3ZnIGlkPSJMYXllcl8xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1MCA1MCI+PGRlZnM+PHN0eWxlPi5jbHMtMXtmaWxsOiMzMzM7c3Ryb2tlLXdpZHRoOjBweDt9PC9zdHlsZT48L2RlZnM+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJtMjUsMEM4LjMzLDAsMCw4LjMzLDAsMjVzOC4zMywyNSwyNSwyNWMxNi42NywwLDI1LTguMzMsMjUtMjVDNTAsOC4zMyw0MS42NywwLDI1LDBaIi8+PC9zdmc+Cg==");
  mask-repeat: no-repeat;
  mask-size: 100%;
}
</style>
